import whyImg from "@/assets/concepts/why-6x.png.asset.json";
import compactSideImg from "@/assets/concepts/compact-side.png.asset.json";
import compactDimsImg from "@/assets/concepts/compact-dims.png.asset.json";
import compactTopImg from "@/assets/concepts/compact-top.png.asset.json";
import aluminumImg from "@/assets/concepts/aluminum.png.asset.json";
import caseImg from "@/assets/concepts/case.png.asset.json";
import collimationImg from "@/assets/concepts/collimation.png";
import coatingsImg from "@/assets/concepts/coatings.png.asset.json";
import decadeImg from "@/assets/concepts/decade.png.asset.json";
import franceImg from "@/assets/concepts/travel-france.png";
import socalImg from "@/assets/concepts/travel-socal.png";
import trainImg from "@/assets/concepts/travel-train.png";
import roadTripImg from "@/assets/concepts/road-trip-line.png";
import roadCoastImg from "@/assets/concepts/road-coast.jpg.asset.json";
import roadSurfImg from "@/assets/concepts/road-surf.jpg.asset.json";
import roadMountainsImg from "@/assets/concepts/road-mountains.jpg.asset.json";
import roadValleyImg from "@/assets/concepts/road-valley.jpg.asset.json";
import roadDolphinsImg from "@/assets/concepts/road-dolphins.jpg.asset.json";
import roadGullsImg from "@/assets/concepts/road-gulls.jpg.asset.json";
import roadOceansideSurfImg from "@/assets/concepts/road-oceanside-surf.jpg.asset.json";
import roadOceansidePierImg from "@/assets/concepts/road-oceanside-pier.jpg.asset.json";

export type ConceptStatus = "outline" | "draft" | "revised" | "final";


export interface ConceptFigure {
  src: string;
  alt: string;
  caption?: string;
  photo?: boolean;
}

export interface ConceptSection {
  /** Heading text — also used to derive the anchor id. */
  heading: string;
  paragraphs: string[];
  /** Optional pull quote rendered beside the section. */
  quote?: { text: string; attribution?: string };
  /** Optional technical illustration rendered under the section text. */
  figure?: ConceptFigure;
  /** Optional gallery of figures rendered side-by-side under the section text. */
  figures?: ConceptFigure[];
}


export interface ConceptPost {
  slug: string;
  /** When true the note is hidden from the index and marked noindex. */
  draft?: boolean;
  title: string;
  summary: string;
  topics: string[];
  status: ConceptStatus;
  /** ISO date the note was first written. */
  written: string;
  /** ISO date the note was last revised. */
  updated: string;
  audience: string[];
  readingMinutes: number;
  /** Two-tone gradient used for the note's generative cover. */
  gradient: [string, string];
  /** Alternate gradient revealed on hover. */
  gradientHover: [string, string];
  /** Engraved illustration used as the note's cover. */
  image: string;
  intro: string;
  sections: ConceptSection[];
}

export const conceptPosts: ConceptPost[] = [
  {
    slug: "why-6x-magnification",
    title: "Why 6x",
    summary:
      "Higher magnification is not better magnification. Why 6x is the most usable power for handheld observation.",
    topics: ["Optics", "Design"],
    status: "final",
    written: "2026-03-02",
    updated: "2026-07-14",
    audience: ["Buyers", "Optics nerds"],
    readingMinutes: 6,
    gradient: ["#1E2A44", "#E8B24C"],
    gradientHover: ["#E8B24C", "#1E2A44"],
    image: whyImg.url,
    intro:
      "Almost every binocular on a shelf is sold on one number. 10x, 12x, sometimes 20x. The assumption is that a bigger number means you see more. We went the other way and built a 6x, and the honest answer for why is that it's the power we actually enjoy using.",
    sections: [
      {
        heading: "What magnification really does",
        paragraphs: [
          "Magnification doesn't just enlarge the thing you're looking at. It enlarges everything — including the small movements of your own hands, your breathing, and your pulse. At 10x, a tiny tremor turns into a visible sway across the whole image. You end up bracing against a railing, a car roof, or a tree just to steady the view long enough to identify what you're looking at.",
          "There's a rough rule most people arrive at on their own after enough time in the field: somewhere between 7x and 8x, handheld viewing starts to get shaky. Below that, the image mostly sits still. At 6x it's genuinely calm. You can look through it standing on a moving boat, walking a trail, or leaning out a train window and the picture holds together.",
        ],
        quote: {
          text: "The most powerful binocular you own is the one you can hold still.",
        },
      },
      {
        heading: "The trade you're actually making",
        paragraphs: [
          "Lower power isn't just less of a good thing. It buys you a handful of properties that matter more day to day than raw reach. The exit pupil — the little disc of light that lands on your eye — is bigger, so the view doesn't black out when your hands shift slightly or when you're wearing glasses. That single detail is the difference between an optic that feels effortless and one that feels fussy.",
          "Depth of field is deeper too. At 6x, a large slice of the scene is in acceptable focus at once, so you're not constantly riding the focus wheel between the boat in the foreground and the headland behind it. You point, you look, you're done.",
          "And low light behaves better than people expect. A 6x16 gathers less total light than a big 42mm objective, but it spreads what it does gather over a wider, brighter exit pupil, so dusk viewing stays usable well past the point where the numbers suggest it shouldn't.",
        ],
      },
      {
        heading: "Field of view is the part people notice",
        paragraphs: [
          "The spec that changes the experience most isn't magnification, it's how much of the world you see at once. Wide field means you find things. The bird that just moved, the buoy in the chop, the line of swell out past the break — you catch it because it's already in the frame, not because you swept back and forth hunting for it through a straw.",
          "Finding is most of the work. Once something is centered, the actual looking is fairly easy. High-power binoculars are very good at the easy part and quite bad at the hard part, which is why they so often stay in the bag.",
        ],
      },
      {
        heading: "Small enough to be there",
        paragraphs: [
          "There's a practical side to this as well. Dropping the power lets everything shrink — shorter barrels, smaller prisms, a body that fits a jacket pocket instead of a dedicated case. A binocular you carry casually gets used ten times more than one you have to plan around.",
          "That's the whole thesis, really. Not that 6x is optically superior in every measurable way, because it isn't. It's that 6x is the power where the instrument stops asking anything of you. It's steady, it's forgiving, it's small, and it's in your bag on the day something interesting happens.",
        ],
      },
    ],

  },
  {
    slug: "aluminum-as-a-material",
    title: "ALUMINUM AS A MATERIAL",
    summary:
      "You can't judge optics through a screen. But you can judge the housing. Machined aluminum, hard anodizing, and why they predict how the view holds up over a decade.",
    topics: ["Materials", "Manufacturing"],
    status: "revised",
    written: "2026-02-11",
    updated: "2026-08-21",
    audience: ["Makers", "Buyers"],
    readingMinutes: 9,
    gradient: ["#8A929E", "#1E2A44"],
    gradientHover: ["#1E2A44", "#8A929E"],
    image: aluminumImg.url,
    intro: "",
    sections: [
      {
        heading: "What a good housing is really protecting",
        paragraphs: [
          "A binocular is two small telescopes that have to keep pointing at exactly the same thing, and if the two optical axes drift even a fraction of a degree apart, your eyes quietly go to work fusing two images that no longer overlap. You won't see anything obviously doubled. You'll just get a headache after twenty minutes and never think to blame the binoculars. Holding those axes parallel through heat, cold, knocks, and years is the housing's entire job.",
          "Almost everything in the compact category is a polymer shell over a metal chassis, and the reason is straightforward economics. An injection mould is expensive once and then produces a body every forty seconds for pennies. Plastic is also forgiving, so if the internal components vary a little from batch to batch, the shell flexes enough to swallow the difference. The catch is that polymer creeps. Left under load at temperature it slowly deforms and doesn't spring back, and a binocular that spends July in a hot car and January in a cold garage sees that cycle over and over. Each pass relaxes the mounts holding the prisms by a few microns. This is why so many decade-old inexpensive binoculars feel subtly wrong without being visibly broken.",
        ],
      },
      {
        heading: "What actually happens to the block",
        paragraphs: [
          "Ours begins as a solid billet of high-grade aluminum, and a CNC mill spends a long time removing everything that isn't a binocular. The tool works in from the outside, roughing away the bulk in passes, then comes back with finer cutters to bring the walls down to final thickness and bore out the seats where the prisms and lens groups will sit. Most of the original block leaves as chips in the bottom of the machine. It is an absurd way to make a part if you're counting pennies and a very good one if you're counting decades.",
          "What it buys is three things. The billet has a uniform grain structure throughout, having been rolled and worked long before it reached the mill, so the material behaves the same everywhere the cutter goes and the part doesn't warp coming off the tool or slowly relax in the years afterward. The seats can be held to a hundredth of a millimeter, which means the optics are located by the housing itself rather than coaxed into place with shims and adhesive. And the walls can be genuinely thin.",
          "That last one is worth sitting with, because it runs against the intuition that metal makes things heavy. Aluminum is roughly one and a half times denser than the polymers used in binocular shells, but it's around fifteen times stiffer, so you need dramatically less of it to reach the same rigidity. Thin walls are only available to you if the material can survive being thin. That single fact is why the 6×16 is 37 mm deep and 145 grams, lighter than most phones, and smaller than the plastic alternative rather than larger.",
        ],
      },
      {
        heading: "Growing a surface instead of painting one",
        paragraphs: [
          "Once the body is machined it gets hard anodized, which people often assume is a fancy paint and isn't remotely. The part goes into an acid bath, a current is run through it, and the outer layer of the aluminum is chemically converted into aluminum oxide — a ceramic, grown down into the surface rather than laid on top of it. Because there's no coating, there's no interface for a coating to fail at and nothing to chip off. The hard layer simply is the part.",
          "Hard anodizing is done cold and slow, which produces a much denser and thicker oxide than the standard decorative version, and it changes how the instrument ages. Color sits in the pores of the oxide, distributed through its depth, so abrasion polishes the surface rather than breaching a layer and exposing raw metal underneath. Scratches lighten it. Ten years in a pocket reads as patina. The oxide is also chemically stable and non-conductive, so the salt spray and sweat that would pit bare aluminum and creep under paint have nothing to get hold of, which matters most in exactly the place that destroys everything else — an open boat.",
        ],
      },
      {
        heading: "The part that's still done by hand",
        paragraphs: [
          "There are over thirty components in an instrument this size, and the final step in Japan is a technician looking through it, judging the merged image, and adjusting until the two axes are truly parallel. Machines can hold the tolerances all day, but that last call is a person deciding when it's right, and no amount of good glass rescues a pair that's slightly off. The glass itself is hand-polished and fully multi-coated on every glass-to-air surface, which is what keeps contrast alive in glare where lesser compacts turn milky.",
          "We should be fair about the trade. Aluminum is cold in your hands in February, and it dents where plastic would flex and bounce back. Drop this on concrete and you'll have a mark on it for good. It'll still be collimated, though, and that was always the point.",
          "Dimensions 77 × 61 × 37 mm. Weight 145 g. Optics 6×16, roof prism, fully multi-coated. Field of view 145 m at 1,000 m (8.3°). Housing machined aluminum billet, hard anodized. . Build 30+ components, hand-assembled in Japan. Warranty 5-year international.",
        ],
      },
      {
        heading: "A few questions we get",
        paragraphs: [
          "Are aluminum binoculars better than plastic ones? For holding alignment over years, clearly yes. Plastic absorbs a hard knock slightly better and costs far less to make. If you expect to still own the thing in ten years, metal is the better bet.",
          "Doesn't metal make them heavier? It doesn't, because you need so much less of it. Ours is 145 g, lighter than most plastic-bodied compacts in the same class.",
          "What actually fails on cheap compacts? Collimation, nearly always. The optics are fine; their alignment isn't. The symptom is eye strain rather than an obviously doubled image, which is why hardly anyone diagnoses it.",
          "\n",
        ],
      },
    ],
  },

  {
    slug: "the-case-for-carrying-less",
    title: "Packaging Design",
    summary:
      "The best optic is the one you actually have with you. A short note on the tyranny of the gear bag.",
    topics: ["Field Notes"],
    status: "draft",
    written: "2026-05-11",
    updated: "2026-05-11",
    audience: ["Anyone packing a bag"],
    readingMinutes: 4,
    gradient: ["#2F5D50", "#E8B24C"],
    gradientHover: ["#E8B24C", "#2F5D50"],
    image: caseImg.url,
    intro:
      "Every piece of equipment has a carry cost, and the carry cost is paid before any of the benefits arrive.",
    sections: [
      {
        heading: "Presence beats specification",
        paragraphs: [
          "A full-size pair of binoculars is superb on a tripod at dawn and irrelevant in a jacket pocket at noon. The measure of a tool is not its specification sheet — it is the number of times it was present at the moment something happened.",
        ],
        quote: {
          text: "Gear you left at home has a resolution of zero.",
        },
      },
      {
        heading: "Designing from the pocket outward",
        paragraphs: [
          "We started with the constraint that the instrument had to disappear until it was needed. The prism layout, hinge geometry, and objective size all followed from that.",
        ],
      },
    ],
  },
  {
    slug: "six-days-in-france",
    title: "Six days in France",
    summary:
      "Notes from a slow trip through Paris and the Dordogne with one small binocular in a jacket pocket.",
    topics: ["Travel", "Field Notes"],
    status: "final",
    written: "2026-06-18",
    updated: "2026-08-12",
    audience: ["Travelers"],
    readingMinutes: 6,
    gradient: ["#1E2A44", "#E8B24C"],
    gradientHover: ["#E8B24C", "#1E2A44"],
    image: franceImg,
    intro:
      "We took a single pair across six days of ordinary sightseeing — no hides, no tripods, no plan more ambitious than walking until something looked interesting.",
    sections: [
      {
        heading: "Cathedrals are detail machines",
        paragraphs: [
          "The facade of a gothic cathedral is designed to be read from a distance you cannot reach. Gargoyles, tympanum carvings, the weathered faces along a rose window — all of it sits forty meters above the square, sized for a viewer who was never going to get closer.",
          "At 6x, standing in the middle of the plaza, the whole upper register becomes legible. You stop photographing the building and start reading it.",
        ],
        quote: {
          text: "Half of European travel is looking at things installed too high to see.",
        },
      },
      {
        heading: "Rooftops and the sixth-floor window",
        paragraphs: [
          "Paris rewards the person who looks up. Zinc roofs, chimney pots, the swifts that scream around the courtyards at dusk — a wide 6x field turns a hotel window into an observation post.",
          "The compact body mattered more than the specification here. It lived in a jacket pocket for the entire trip and came out a dozen times a day.",
        ],
      },
      {
        heading: "Out of the city",
        paragraphs: [
          "In the Dordogne the same instrument covered river valleys, cliff villages, and a kestrel working a field edge at forty meters. Nothing about the trip was a birding trip, and yet the bird list ran to nineteen species.",
          "That is the argument for carrying an optic that asks nothing of you: it turns incidental hours into observation.",
        ],
      },
      {
        heading: "What we would pack again",
        paragraphs: [
          "One binocular, the strap, the pouch. No case, no cleaning kit beyond a cloth. Anything else stays in the bag and therefore stays in the room.",
        ],
      },
    ],
  },
  {
    slug: "local-birding-southern-california",
    title: "Local birding in Southern California",
    summary:
      "Coastal sage scrub, canyon edges, and estuaries — how a 6x binocular performs on the birds within twenty minutes of home.",
    topics: ["Travel", "Field Notes", "Optics"],
    status: "revised",
    written: "2026-05-02",
    updated: "2026-08-15",
    audience: ["Birders", "Anyone with a morning free"],
    readingMinutes: 7,
    gradient: ["#2F5D50", "#E8B24C"],
    gradientHover: ["#E8B24C", "#2F5D50"],
    image: socalImg,
    intro:
      "Southern California is unusually generous to the casual observer: chaparral, canyon, estuary and open coast often sit within a single morning's drive, and most of the interesting birds are close.",
    sections: [
      {
        heading: "The birds here are near, not far",
        paragraphs: [
          "Scrub-jays, wrentits, California towhees, Anna's hummingbirds and gnatcatchers work the sage at ten to twenty meters. At that range high magnification is a liability — the field of view collapses and the near focus limit fights you.",
          "A wide 6x field finds a moving bird in dense brush on the first try. Finding is the hard part; magnification only matters after you have the bird.",
        ],
        quote: {
          text: "Most missed birds are not too small to see. They are too fast to find.",
        },
      },
      {
        heading: "Canyon mornings",
        paragraphs: [
          "Start early, walk a canyon bottom with the sun behind you, and stop more than you walk. The deep depth of field at 6x means you rarely touch the focus wheel between a shrub at eight meters and a ridge line at three hundred.",
        ],
      },
      {
        heading: "Estuaries and the light problem",
        paragraphs: [
          "Bolsa Chica, Batiquitos, the Ballona wetlands — shorebirds at midday sit in punishing glare off wet mud. The generous exit pupil and fully multi-coated train keep contrast intact where cheaper optics wash out into gray.",
        ],
      },
      {
        heading: "A one-pocket kit",
        paragraphs: [
          "Binocular, water, a phone for the checklist. Local birding fails when it becomes an expedition; it works when it is something you can decide to do at 6:40 in the morning without packing.",
        ],
      },
    ],
  },
  {
    slug: "testing-on-the-road",
    title: "FIELD TESTING IN CA",
    summary:
      "How the 6×16 handles coastal observation, moving wildlife, and low-contrast mountain light.",
    topics: ["Travel", "Field Notes"],
    status: "draft",
    written: "2026-08-21",
    updated: "2026-08-26",
    audience: ["Travelers", "Field testers"],
    readingMinutes: 4,
    gradient: ["#2F5D50", "#1E2A44"],
    gradientHover: ["#1E2A44", "#2F5D50"],
    image: roadTripImg,
    intro:
      "Field testing is about whether an optic gets used. The 6×16 was carried in the car, at coastal overlooks, on the water, and in the Eastern Sierras to see where low power and low weight actually matter.",
    sections: [
      {
        heading: "Always within reach",
        paragraphs: [
          "At 145 grams, the 6×16 lives on the dash, in a door pocket, or in a jacket with keys and a phone. No case or setup means it gets picked up when a detail appears. There's really never a question of whether to take this or leave them behind because of their size.",
          "The practical test is seeing if an optic used day after day becomes annoying, cumbersome, or fails to deliver in the day to day of dust, sun, oils, sunscreens, the glovebox, pocket, and everything else that gets thrown at it while making the details feel closer. \n\n\nHere were some of our favorite use cases while driving around the State in search of waves, mountains, animals, and serenity. ",
        ],
        quote: {
          text: "A binocular you have to decide about is a binocular you leave in the car.",
        },
        figure: {
          src: roadCoastImg.url,
          alt: "Coast highway curving above the Pacific in late afternoon light",
          caption: "COASTAL PULLOUT — QUICK ACCESS, WIDE VIEWS",
          photo: true,
        },
      },
      {
        heading: "SCOPING THE LINEUP",
        paragraphs: [
          "The 6×16 is enough magnification to read the lineup, see who's getting the good ones, and watch your friend get the wave of the day. On a day like this one at Swamis in San Diego, extended use of the 6x16's is well warranted. Surf checks when the waves are good become a lot more interesting to watch when you can hone in on the details or wave-riding. ",
        ],
        figure: {
          src: roadSurfImg.url,
          alt: "Surfers sitting in a lineup as a clean wave breaks offshore",
          caption: "SURF LINEUP — READING THE BREAK FROM SHORE",
          photo: true,
        },
      },
      {
        heading: "Oceanside detail",
        paragraphs: [
          "From shore, the same wide field follows individual surfers and picks out pier structure without a tripod.",
        ],
        figures: [
          {
            src: roadOceansideSurfImg.url,
            alt: "A surfer dropping into a green wave at Oceanside",
            caption: "SURF BREAK DETAIL FROM SHORE",
            photo: true,
          },
          {
            src: roadOceansidePierImg.url,
            alt: "The Oceanside pier stretching over the Pacific",
            caption: "OCEANSIDE PIER — STRUCTURE AT A DISTANCE",
            photo: true,
          },
        ],
      },
      {
        heading: "Tracking moving wildlife",
        paragraphs: [
          "Six-power magnification keeps a moving subject in frame while handheld. On the water, it was used to track tuna breaking the surface and birds working above them. The wide field makes it faster to find and follow action. \n\n\nWhile most people use Gyro binoculars for spotting far away fish, the 6x16's earn there place on the boat with their wide field of view and naturally stable characteristics. Not to mention, holding 145 grams up to your face is significantly easier than a 4lb gyro. ",
        ],
        figure: {
          src: roadDolphinsImg.url,
          alt: "Tuna breaking the surface offshore with seabirds above them",
          caption: "TUNA OFFSHORE — TRACKING MOVEMENT HANDHELD",
          photo: true,
        },
      },
      {
        heading: "Eastern Sierra light",
        paragraphs: [
          "In the Eastern Sierras, the 6×16 helped us investigate distant ridge lines, blanketed by snowfall. The shift from a hot car to cold, dry elevation also tested whether the housing and optical alignment stayed stable through sharp temperature changes throughout.",
        ],
        figure: {
          src: roadMountainsImg.url,
          alt: "Snow-covered ridgelines in the Eastern Sierras under low cloud",
          caption: "EASTERN SIERRAS — DISTANT RIDGES AND LOW CONTRAST",
          photo: true,
        },
      },
      {
        heading: "Low contrast",
        paragraphs: [
          "Overcast mountain light is unforgiving. The test is whether small tonal differences stay visible: a tree line, a snowfield edge, or an animal against the valley floor. The 6×16's coatings provide beautiful contrast even down to low light conditions. ",
        ],
        figure: {
          src: roadValleyImg.url,
          alt: "Snow-covered Eastern Sierra mountain flank above a bare valley floor",
          caption: "EASTERN SIERRAS — DETAIL IN FLAT LIGHT",
          photo: true,
        },
      },
      {
        heading: "Field use",
        paragraphs: [
          "Coast, water, and mountain use in a single trip: the common thread is that the binocular was reached for before the moment passed.",
        ],
        figure: {
          src: roadGullsImg.url,
          alt: "Two gulls in flight against a bright open sky",
          caption: "FIELD USE — A SHORT LIST OF REAL OBSERVATIONS",
          photo: true,
        },
      },
    ],
  },
  {
    slug: "observing-from-a-moving-window",
    title: "Observing from a moving window",
    summary:
      "Trains, ferries and back seats — how to actually see something from a vehicle that will not hold still.",
    topics: ["Travel", "Field Notes"],
    status: "draft",
    written: "2026-07-09",
    updated: "2026-07-09",
    audience: ["Travelers"],
    readingMinutes: 4,
    gradient: ["#6C7A92", "#1E2A44"],
    gradientHover: ["#1E2A44", "#6C7A92"],
    image: trainImg,
    intro:
      "A train window is one of the few places where a high-power binocular is definitively worse than a low-power one.",
    sections: [
      {
        heading: "Motion is multiplied too",
        paragraphs: [
          "Everything magnification does to detail it also does to vibration and to the angular speed of the foreground. At 10x, trackside scenery smears; at 6x, the middle distance stays readable and the far hills are steady.",
        ],
      },
      {
        heading: "Look far, not near",
        paragraphs: [
          "The trick from any moving vehicle is to work the horizon. Distant objects have low angular velocity, so a farmhouse two kilometers out sits nearly still while the hedge beside the track is gone before you focus.",
        ],
      },
      {
        heading: "Ferries are the best seat in Europe",
        paragraphs: [
          "Fjord crossings, channel ferries, harbor shuttles — the deck is stable, the light is open, and there is always something worth resolving: a navigation mark, a distant hull, a raft of seabirds off the bow.",
        ],
      },
    ],
  },
  {
    slug: "collimation-and-why-it-matters",
    title: "Collimation, and why it matters",
    summary:
      "What happens inside a binocular when the two optical axes stop agreeing — and why you feel it before you see it.",
    topics: ["Optics", "Manufacturing"],
    status: "revised",
    written: "2026-01-20",
    updated: "2026-08-25",
    audience: ["Optics nerds", "Service techs"],
    readingMinutes: 7,
    gradient: ["#1E2A44", "#6C7A92"],
    gradientHover: ["#6C7A92", "#1E2A44"],
    image: collimationImg,
    intro:
      "A pair of binoculars is really two small telescopes strapped together, and the whole thing only works if both of them are pointed at exactly the same spot. Not close to the same spot. The same spot. When they're lined up, your brain takes the two images and merges them into one without you noticing. That merging is something your eyes do all day anyway, so it costs you nothing. Lining those two tubes up is called collimation, and it's one of the most important aspects of a binocular's quality and ability to last generations.",
    sections: [
      {
        heading: "You feel it before you see it",
        paragraphs: [
          "When the two tubes drift out of alignment, you usually don't see a double image. Your eyes just quietly work harder, pulling the two views together so the picture still looks normal.",
          "The error is tiny — small fractions of a degree — but your eye muscles are holding that correction the entire time you're looking. After twenty minutes you get a headache, or your eyes feel tired, or you just find yourself not really wanting to use them. Almost nobody connects that back to the binoculars, because the view looked fine.",
          "So a misaligned pair doesn't announce itself. It just becomes the pair you stop reaching for due to a subtle misalignment. ",
        ],
      },
      {
        heading: "Set by hand, then protected by the body",
        paragraphs: [
          "Alignment gets dialed in at the end of assembly. An experienced assembler and technician looks through the finished instrument, judges the merged image, and adjusts until the two axes are genuinely parallel using special instruments and consistent visual targets. It's an experienced making the call, not a machine hitting a number, since these instruments are hand assembled by experienced craftsmen in Japan. ",
          "After that, the only thing keeping it there is the housing. Every knock, every hot car, every minor bump and temperature / pressure change puts a small load on the structure holding the prisms in place. A rigid body absorbs that and the alignment stays put. A flexible one lets it move a little at a time, and hands the problem back to you a few years later.",
          "This is the practical reason to care what a binocular is made of. It isn't about scratches.",
        ],
      },
      {
        heading: "No amount of good glass fixes it",
        paragraphs: [
          "You can put excellent lenses and the best coatings available into a pair, and none of it helps if the two sides disagree about where they're pointing. Coatings affect how bright and clear the image is. Collimation decides whether looking through it is comfortable at all.",
          "It's the specification you can't see in a photo, can't read off a box, and notice immediately when it's missing.",
        ],
      },
    ],
  },
  {
    slug: "notes-on-coatings",
    title: "NOTES ON WHAT TO LOOK FOR WHEN BUYING BINOCULARS",
    summary:
      "Exit pupil, prism glass, coatings, eye relief — what every spec on a binocular box actually does to the view, written for people who aren't optical engineers.",
    topics: ["Optics"],
    status: "final",
    written: "2026-03-19",
    updated: "2026-08-20",
    audience: ["Buyers"],
    readingMinutes: 11,
    gradient: ["#3B2F6E", "#E8B24C"],
    gradientHover: ["#E8B24C", "#3B2F6E"],
    image: coatingsImg.url,
    intro:
      "There are two numbers on the front of every binocular, and neither one tells you whether you'll like looking through it. Everything that actually decides that — how bright it is at dusk, whether it gives you a headache after twenty minutes, whether you can find a bird before it moves — is buried in a spec table nobody explains. So here's the whole instrument, part by part, in the order you'd meet it. We'll use our own 6×16 as the worked example, including the places where it's the wrong tool.",
    sections: [
      {
        heading: "The two numbers on the box",
        paragraphs: [
          "6×16 means six times magnification, sixteen millimeter objective lenses. First number, how much closer things look. Second number, how wide the front glass is. Nearly every buying mistake starts here, because both numbers look like more is better and neither one is.",
          "Magnification multiplies your hand tremor along with the image. At 6×, your pulse is invisible in the view. At 10×, you can see it — a slow wobble that never quite settles. At 12× and up you genuinely need something to lean on. Magnification also narrows the field of view and shrinks the exit pupil, both of which make the binocular harder to use.",
          "Objective diameter controls how much light gets in. Bigger front lenses gather more, which is why serious low-light optics are 42 mm or 50 mm. It's also why they weigh 800 grams. Glass is heavy and there's no way around it.",
          "The right question isn't which numbers are highest. It's which numbers match how you'll actually use this.",
        ],
      },
      {
        heading: "Exit pupil: the spec that predicts brightness",
        paragraphs: [
          "This is the one to learn. Divide the objective by the magnification: 16 ÷ 6 = 2.7 mm exit pupil. The exit pupil is the little disc of light that lands on your eye. Hold any binocular at arm's length and you can see it floating in the eyepiece.",
          "Your own pupil is about 2–3 mm in bright daylight and opens to 5–7 mm in the dark, less as you age. If the exit pupil is bigger than your pupil, the extra light is wasted — it hits your iris. If it's smaller, you're the bottleneck and the view looks dim.",
          "So in daylight, a 2.7 mm exit pupil delivers essentially everything your eye can accept. A 10×42 has a 4.2 mm exit pupil and looks no brighter at noon, because your pupil isn't open wide enough to use it. At dusk that flips completely: your pupil opens to 5 mm and the 42 mm instrument starts feeding it more light than ours can. That's a real difference and we're not going to talk around it.",
          "A large exit pupil also makes a binocular more forgiving. With 4+ mm you can hold it slightly off-axis and still see a full image. With 2.7 mm, alignment is fussier — you'll notice this the first few times, then stop noticing it permanently.",
          "Two related numbers you'll see quoted: relative brightness, the exit pupil squared, which is 7.3 for ours; and twilight factor, the square root of magnification × objective, which is 9.8. Both are rough proxies, and neither accounts for coating quality, which in practice moves the view more than either figure.",
        ],
        quote: {
          text: "If the exit pupil is bigger than your pupil, the extra light is wasted — it hits your iris.",
        },
      },
      {
        heading: "Field of view: how you find things",
        paragraphs: [
          "Quoted two ways, and they're the same fact: 8.3° true field is 145 m at 1,000 m. Multiply degrees by 17.45 to get meters at a kilometer. That's the whole conversion.",
          "Wide field is the most underrated spec in the category, because finding a subject is most of the work. Something moves at the edge of your vision, you get the binoculars up, and either it's in the circle or it isn't. A narrow high-power view is excellent at the easy part of the job — studying something that's already sitting still — and bad at the hard part.",
          "Field of view is inversely tied to magnification. Push the power up and the circle closes down. An 8.3° field on a 6× is genuinely wide; most 8×25 compacts sit around 6.5–7.0°, and a lot of 10×42s are under 6.5°. You'll also see apparent field of view, which is true field multiplied by magnification — roughly how big the circle feels once you're looking through it. Marketing departments prefer this number because it's bigger.",
        ],
      },
      {
        heading: "Eye relief: the spec for glasses wearers",
        paragraphs: [
          "Eye relief is how far behind the eyepiece your eye can sit and still see the whole field. Ours is 14 mm. If you wear glasses, your eye is already held about 12–15 mm back by the lenses. Too little eye relief and you get tunnel vision — a full-field view is physically unreachable with your glasses on.",
          "Straight talk: 14 mm is workable but not generous. Fold the eyecups down and most glasses wearers get the full field. Thick frames or high-index lenses that sit further out, and it'll be tight. If you never take your glasses off and want margin, look for 16 mm or more. That's a real consideration and it's better said than discovered. Without glasses, 14 mm is plenty, and the eyecups extend to set the spacing for you.",
        ],
      },
      {
        heading: "Interpupillary distance: the spec nobody checks",
        paragraphs: [
          "IPD is the hinge range — how far apart the two barrels can be set. Ours is 30–82 mm. Adult eyes typically sit 58–72 mm apart. Children run 45–55 mm, and small children lower still. Most compacts bottom out near 56 mm, which is why handing a pair to a seven-year-old usually ends with them seeing two dark circles and giving it back.",
          "A 30 mm minimum means the same instrument works across a whole family. It's an unglamorous spec that decides whether the binocular gets shared or not.",
        ],
      },
      {
        heading: "Close focus: the near end of the range",
        paragraphs: [
          "The nearest distance at which the instrument can still focus. Ours is 3.2 m. That's fine for general use and unremarkable for the category. If you're into butterflies, dragonflies, or reading museum placards from across a room, dedicated close-focus binoculars get down to 1.5–2 m and you'd want one of those instead. Worth knowing before you buy rather than after.",
        ],
      },
      {
        heading: "Prisms: why binoculars aren't just two telescopes",
        paragraphs: [
          "A simple lens tube gives you an upside-down, mirror-reversed image. Prisms flip it back. Which prism you use determines the entire shape of the instrument.",
          "Porro prisms offset the light path, which is why old-style binoculars have that stepped, zigzag body with objectives set wider than the eyepieces. Optically efficient and cheaper to make well — but bulky, and the shape rules out a pocket.",
          "Roof prisms fold the path so the objective and eyepiece sit in a straight line. That's what makes a binocular slim and pocketable. The cost is manufacturing difficulty: roof prisms demand tighter tolerances and additional coatings to perform as well as a Porro. A cheap roof prism is worse than a cheap Porro. A good one is what lets a real binocular fit in a shirt pocket. Ours is a roof prism, because the whole design brief was pocketable.",
        ],
      },
      {
        heading: "BaK-4 vs BK-7, and why that argument is about Porros",
        paragraphs: [
          "You'll run into this fast, usually as BaK-4 good, BK-7 bad. It's more specific than that. BK-7 is borosilicate crown glass. BaK-4 is barium crown, with a higher refractive index. In a Porro prism, that index difference is genuinely important: BK-7's lower index means light hitting the prism face at a steep angle escapes instead of reflecting internally, which clips the edge of the exit pupil. Look through a cheap BK-7 Porro and the bright disc is visibly squared-off at the sides, and the edges of the view go grey.",
          "In a roof prism, that specific failure doesn't happen the same way. Roof prisms use a different internal geometry, and the exit pupil truncation that gives BK-7 its bad name in Porros isn't the governing problem. What actually determines a roof prism's performance is the reflective coating on the roof surfaces, the phase correction, and the precision of the roof edge itself.",
          "So BK-7 in a roof prism, at a 2.7 mm exit pupil, in a well-figured train is a legitimate engineering choice, not a corner cut. In a Porro at 4 mm exit pupil it would be a real compromise. Same glass, different verdict, because the job is different.",
        ],
      },
      {
        heading: "Coatings: where most of the money goes",
        paragraphs: [
          "This is the part that separates optics that look alike on paper. Every time light crosses a glass-to-air surface, about 4% of it reflects away. That light isn't just lost — it bounces around inside the tube and comes back as veiling glare, which is what makes a cheap binocular look washed-out and milky when you're pointed anywhere near the sun or a bright water surface.",
          "A binocular has a lot of these surfaces. Uncoated, you can lose 40–50% of the light before it reaches your eye. Anti-reflective coatings are microscopically thin layers of metal fluoride, tuned so reflections cancel each other out. Get it right and a surface drops from 4% loss to well under 1%.",
        ],
      },
      {
        heading: "The four-rung ladder the industry blurs",
        paragraphs: [
          "Coated means a single layer on at least one surface — nearly meaningless. Fully coated means a single layer on every glass-to-air surface. Multi-coated means multiple layers on some surfaces; it sounds better than fully coated and often isn't. Fully multi-coated means multiple layers on every glass-to-air surface. That's the one you want.",
          "Read those twice. Multi-coated and fully coated both sound like the top rung and neither is. This is the single most common place buyers get quietly downgraded, and it's completely legal because every word is technically accurate.",
          "The difference between the bottom and top of that ladder is roughly 20–30% of total light transmission, plus a large change in how the image holds up against backlight. It's the most visible difference in the whole instrument.",
        ],
        quote: {
          text: "\u201CMulti-coated\u201D and \u201Cfully coated\u201D both sound like the top rung. Neither is.",
        },
      },
      {
        heading: "Phase correction and reflective coatings",
        paragraphs: [
          "When light splits and recombines across a roof prism's two faces, the two halves come back slightly out of phase. The result is a small but real loss of resolution and contrast — an image that's technically sharp but looks slightly soft. Phase correction coating on the roof surfaces re-aligns them. It only applies to roof prisms; Porros don't have the problem. If you're comparing two roof-prism binoculars and one is phase-corrected, that's a meaningful gap.",
          "One face of a roof prism also can't rely on total internal reflection, so it needs a mirror coating. Three grades: aluminum, around 87–93% reflectivity and cheapest; silver, around 95–98%; and dielectric, 99%+ using dozens of layers, most expensive and best at preserving color neutrality. This coating sits in the light path for every photon that reaches your eye, so a few percent here is worth more than a few percent almost anywhere else.",
          "Exterior coatings aren't optical but they matter in practice. Hydrophobic coatings on the outer lens surfaces shed rain and make fingerprints wipe off in one pass rather than smearing. Hard coats resist the fine scratching that comes from cleaning a lens in the field with whatever's in your pocket.",
        ],
      },
      {
        heading: "The body: what happens over ten years",
        paragraphs: [
          "Almost every compact binocular is a polymer shell over a metal chassis. Polymer moulds cheaply and forgives loose tolerances. It also creeps. Over years of hot cars and cold garages, the geometry holding the prisms in alignment slowly relaxes. The two barrels drift out of parallel — collimation error — and your eyes silently fight to merge two images that no longer line up. That's the headache you can't explain from a decade-old pair, and it's usually not repairable at that price point.",
          "Machined aluminum billet holds its geometry through the same heat cycles. It's also stiff enough to allow thin walls, which is the only reason a metal-bodied binocular can be as small as a polymer one instead of larger.",
          "Anodizing converts the aluminum surface into a hard oxide layer with the color set inside it, rather than a paint film sitting on top. Scratches lighten the finish instead of peeling it. Ten years in a pocket reads as patina.",
        ],
      },
      {
        heading: "Focus, diopter, and assembly",
        paragraphs: [
          "A central focus wheel moves both barrels together. Test it before you buy: it should be smooth in both directions with no gritty spots and no backlash — that dead zone where you reverse direction and nothing happens for a quarter turn. Backlash is the sign of a loose mechanism and it gets worse.",
          "The diopter ring corrects for the difference between your two eyes, and most people never set it. Do it once: cover the right objective, focus on something detailed with the wheel, cover the left, adjust only the diopter until it's equally sharp. Note the index mark. That's your setting permanently.",
          "A binocular this size is over 30 components — prisms, lens groups, spacers, the focus assembly, the hinge — and every optical surface is polished and centered by hand. Alignment is the whole game. Two barrels a fraction of a degree out of parallel produce eye strain that no amount of good glass fixes.",
        ],
      },
      {
        heading: "The short version",
        paragraphs: [
          "Exit pupil, objective divided by magnification, predicts brightness better than either number alone. Under 3 mm is a daylight instrument. Fully multi-coated is the phrase to look for; multi-coated and fully coated are the lower rungs.",
          "Field of view decides whether you find things, and wide beats powerful for most real use. Eye relief of 14 mm is workable with glasses, 16 mm and up is comfortable. Roof prisms need phase correction and good reflective coatings; Porros don't — ask. And choose a metal body over polymer if you expect to own it in ten years.",
        ],
      },
      {
        heading: "Questions we get a lot",
        paragraphs: [
          "What magnification should I buy? 6× to 8× for handheld general use. Above 8× you'll see your own pulse in the image and the field narrows enough that finding things gets slow. Above 10×, plan on a tripod or a rest.",
          "Is a bigger objective always better? Only in low light, and only if your pupil is open wide enough to use it. In daylight a 16 mm and a 42 mm objective deliver a similar-looking image; the 42 mm just weighs five times more.",
          "Does BK-7 mean the binocular is cheap? Not by itself. The BaK-4 versus BK-7 argument comes from Porro prisms, where the lower refractive index visibly clips the exit pupil. In a roof prism the reflective and phase coatings matter more than the substrate.",
          "Do I need waterproof binoculars? If you'll be on boats or in real weather, yes, and check whether it's nitrogen or argon purged — that's what prevents internal fogging, and it's a different feature from being sealed against water.",
          "How much do I need to spend? Below roughly $150 you're generally looking at polymer housings and partial coatings. The jump that changes the view is housing material and coating grade, not magnification.",
        ],
      },
      {
        heading: "The Double Lion 6×16, in these terms",
        paragraphs: [
          "6× magnification, handheld steady with no visible tremor. 16 mm objectives, a daylight instrument with minimal weight. A 2.7 mm exit pupil: full brightness in daylight, limited at dusk. An 8.3° true field, 145 m at 1,000 m — wide, and fast to find things with.",
          "14 mm eye relief, workable with glasses and the eyecups folded. A 30–82 mm interpupillary range that fits children and adults. 3.2 m close focus for general use, not a butterfly optic. A BK-7 roof prism for a straight-line, pocketable body. Multi-coated, hand-polished glass. A machined aluminum billet housing, anodized, that holds collimation over decades. 145 grams, under the carry-it-always threshold, from 30+ components hand-aligned in Japan.",
          "Everything on that list follows from one decision: it had to fit in a shirt pocket. Once you commit to that, 6× and 16 mm aren't compromises — they're the only combination that gets you a usable, steady, wide-field view inside the envelope.",
        ],
      },
    ],
  },
  {
    slug: "designing-for-a-decade",
    title: "Designing for a decade",
    summary:
      "Serviceability, spare parts, and the unglamorous decisions that determine whether an object survives its warranty.",
    topics: ["Design", "Manufacturing"],
    status: "outline",
    written: "2026-02-05",
    updated: "2026-02-05",
    audience: ["Makers"],
    readingMinutes: 5,
    gradient: ["#1E2A44", "#B45B3E"],
    gradientHover: ["#B45B3E", "#1E2A44"],
    image: decadeImg.url,
    intro:
      "Longevity is not a material property. It is a set of decisions about fasteners, adhesives, and whether a technician can open the object without destroying it.",
    sections: [
      {
        heading: "Screws where glue would be faster",
        paragraphs: [
          "We chose screws where adhesive would have been quicker, and standard seals where a bespoke gasket would have looked cleaner. Both decisions cost money and neither is visible in a photograph.",
        ],
      },
      {
        heading: "The 2036 test",
        paragraphs: [
          "The test we apply is simple: in 2036, can this be repaired by someone we have never met, using parts we still know how to make?",
        ],
        quote: {
          text: "A warranty is a promise about manufacturing decisions made years earlier.",
        },
      },
    ],
  },
  {
    slug: "redefining-what-a-compact-binocular-is",
    title: "Redefining what a Compact Binocular is",
    summary:
      "The best compact binoculars aren't actually compact. Most of the category weighs 400 grams. Here's how we'd shop it, and why the Double Lion 6x16 is 77 mm and 145 g.",
    topics: ["Design", "Optics"],
    status: "final",
    written: "2026-08-20",
    updated: "2026-08-20",
    audience: ["Buyers", "Travelers"],
    readingMinutes: 8,
    gradient: ["#1E2A44", "#E8B24C"],
    gradientHover: ["#E8B24C", "#1E2A44"],
    image: compactSideImg.url,
    intro:
      "Go search best compact binoculars and look at what comes back. Half of it weighs 400 grams. A lot of it is the size of a paperback. It gets called compact because the objectives are 25 mm instead of 42 mm, which is a comparison nobody shopping for a pocket binocular is actually making. We got tired of this. So before we designed anything, we picked a number and refused to move off it: the thing has to live in a shirt pocket. Not a bag, not a glovebox, not a padded case with a carabiner. A pocket. Everything else about the Double Lion 6x16 came out of holding that line.",
    sections: [
      {
        heading: "Four questions worth more than the spec sheet",
        paragraphs: [
          "Ignore the magnification on the box for a minute. Ask these instead. One: does it fit the pocket you actually wear? Not a theoretical cargo pocket — the one on the jacket you leave the house in.",
          "Two: is it under about 200 grams? Below that you stop deciding whether to bring it. Above it, you start weighing the trip against the hassle. Three: can you hold the picture still, unsupported? Try it in the shop. At 10x your pulse shows up in the image. Four: can you find a moving thing on the first try? Finding is most of the work, and everyone forgets this.",
          "Fail question one and it isn't a compact binocular. It's a small full-size binocular that needs a bag, which is a completely respectable thing to own — just not the thing you were shopping for.",
        ],
        figure: {
          src: compactSideImg.url,
          alt: "Technical side elevation drawing of the Double Lion 6x16 binocular",
          caption: "Fig. 1 — Side elevation. 77 mm end to end.",
        },
      },
      {
        heading: "What compact looks like next to the alternatives",
        paragraphs: [
          "Numbers are easier than adjectives. Ours: 77 mm long, 145 g, shirt pocket, 8.3° of field — 145 m at a kilometer. The 8x25 class sold as compact runs around 110 mm and 300 g, fits a jacket barely, and gives you 6.5–7.0°. An 8x32 mid-size is about 125 mm and 550 g and doesn't go in a pocket at all. A 10x42 is roughly 142 mm and 780 g with 6.0–6.5° of field.",
          "Folded down, the 6x16 is about the volume of a deck of cards. The gap between that and the 8x25 class isn't a refinement. It's a different category wearing the same label.",
        ],
        quote: {
          text: "Compact is not a marketing word. It is 77 millimeters and 145 grams.",
        },
      },
      {
        heading: "Why the whole category drifted bigger",
        paragraphs: [
          "Binoculars get sold on two numbers, magnification and objective diameter, and both of them reward inflation. A 10x50 sounds like more binocular than an 8x32, and on paper it is. What the paper can't tell you is that the 10x50 stays home.",
          "So you end up with a shelf full of things that are compact only compared to the biggest item in the same catalog. They still want a neck strap, a case, and a small decision every time you leave the house. That last one is the killer. Friction is what decides whether an optic gets used, and no spec sheet has a row for it.",
          "We'd rather lose the argument on paper. A 6x16 in your pocket beats a 12x50 in the closet by an infinite margin, and we're comfortable with how obvious that sounds.",
        ],
      },
      {
        heading: "What you give up, honestly",
        paragraphs: [
          "Sixteen millimeters of objective is sixteen millimeters. At dusk, in heavy tree cover, or twenty minutes after sunset, a good 8x42 will show you things ours won't. If your main use is a dawn hide or serious low-light glassing, buy the big ones. We're not going to pretend otherwise to make a sale.",
          "You also give up reach. Six power is six power. If you need to read a band on a bird's leg across a lake, this is the wrong tool.",
        ],
      },
      {
        heading: "What you get back, which is more than people expect",
        paragraphs: [
          "The image holds still. At 6x your hand tremor doesn't get magnified into visible sway. No bracing against a fence post, no propping your elbows. It's the most underrated property in a travel binocular and it never shows up in reviews.",
          "Depth of field does the focusing for you. Something at eight meters and a ridge at three hundred are both usably sharp. You'll go whole afternoons without touching the wheel.",
          "And the view is wide. 8.3° true field works out to 145 m at a kilometer, and it's the first thing people comment on. Wide field is how you find things. A narrow, high-power view is excellent at the easy half of the job and bad at the hard half. The BK-7 roof prism train is fully multi-coated on every glass-to-air surface, which is what keeps contrast alive in the glare coming off water or wet mud — exactly where cheap compacts turn milky.",
        ],
        figure: {
          src: compactDimsImg.url,
          alt: "Dimensioned front elevation drawing of the Double Lion 6x16",
          caption: "Fig. 2 — Front elevation with dimensions.",
        },
      },
      {
        heading: "The plastic thing nobody brings up",
        paragraphs: [
          "Nearly every compact on the market is a polymer shell over a metal chassis. Polymer is cheap to mould and forgiving if your tolerances are loose. It also creeps. Over years of sitting in hot cars and cold garages, the geometry holding the prisms in line quietly relaxes, and collimation walks off. This is why a decade-old cheap compact often gives you a headache and you can't say why.",
          "Ours is machined out of aluminum billet and anodized, so the color lives in the metal instead of sitting on top of it. Scratches lighten it rather than peeling it. Ten years in a pocket reads as patina.",
          "That choice is also the only reason the walls can be thin enough to hit 37 mm of depth. Metal is the one material that's small, stiff, and stable at the same time.",
        ],
      },
      {
        heading: "Picking the best compact binoculars for what you actually do",
        paragraphs: [
          "Travel: weight and volume are the whole spec. Anything over 200 g gets abandoned in the hotel room by day three. Six power also handles cathedral ceilings and museum interiors, where high magnification is unusable handheld anyway.",
          "Birding: the standard advice is 8x42, and it's right for a planned morning in a hide. It's wrong for the other ninety percent of birding, which happens on an ordinary walk with no plan at all.",
          "Hiking with kids: this one comes down to interpupillary range and almost nobody checks it. Ours adjusts from 30 to 82 mm, so the same pair fits a seven-year-old and an adult. Most compacts bottom out around 56 mm, which is simply too wide for a small face — the kid sees two circles and hands it back.",
          "Concerts and stadiums: pocketable plus steady beats powerful every time when you're seated and can't brace.",
        ],
        figure: {
          src: compactTopImg.url,
          alt: "Top plan drawing of the Double Lion 6x16 showing the central focus wheel and hinge bridge",
          caption: "Fig. 3 — Plan view. Central focus wheel and hinge bridge.",
        },
      },
      {
        heading: "Double Lion 6x16, every number",
        paragraphs: [
          "Magnification 6x. Objective diameter 16 mm. Length, width, depth: 77 mm / 61 mm / 37 mm. Weight 145 g. Exit pupil 2.7 mm. Eye relief 14 mm. Interpupillary range 30–82 mm. Close focus 3.2 m. True field of view 8.3°, or 145 m at 1,000 m. BK-7 roof prism, fully multi-coated. Machined aluminum billet housing, anodized. Five-year international warranty. Made in Japan.",
          "None of that comes from shrinking a normal binocular. You get there by picking 6x and 16 mm first, then building a metal housing around the smallest roof-prism train that'll hold collimation. Start anywhere else and you end up back at 300 grams.",
        ],
      },
      {
        heading: "Questions we get a lot",
        paragraphs: [
          "What actually counts as a compact binocular? Our rule: it fits a shirt or jacket pocket without pulling the fabric out of shape. In numbers, that's roughly under 100 mm long and under 200 g. Most binoculars sold as compact — including the very common 8x25 class — miss both.",
          "Are compact binoculars worth it, or should I just get full-size? Depends entirely on whether you'll carry them. A full-size 8x42 is a better instrument. A compact is a better companion. If your binoculars currently live in a drawer, the better instrument is doing you no good at all.",
          "What magnification is best for compact binoculars? 6x to 8x. Past 8x, your hands shake enough that you can see it, and the field of view narrows to the point where finding the thing takes longer than looking at it.",
          "Are compact binoculars good for birding? For walking-around birding, yes — a wide field is what gets you on a moving bird the first time. For a dedicated dawn session from a hide, a full-size 8x42 will still beat them on light.",
          "Is a 16 mm objective too small? It's smaller, and in genuinely low light you'll notice. In daylight the 2.7 mm exit pupil is plenty, and fully multi-coated surfaces do most of the heavy lifting for contrast.",
          "How much should I spend on compact binoculars? Under about $150 you're generally getting a polymer housing and thin coatings. The money that matters goes to housing material and coating quality — not to a bigger number on the front.",
          "So which one is best? Whichever one is still on you when something happens. That's not a dodge — it's the only criterion that survives contact with real life. Everything else is a tiebreaker. We built the 6x16 against those four questions instead of against a spec sheet. 77 mm, 145 grams, steady in one hand, 145 meters wide at a kilometer. Compact by the strict reading of the word.",
        ],
        quote: {
          text: "The best compact binocular is the one still in your pocket at the moment something happens.",
        },
      },
    ],

  },
];

/** Notes visible on the site — drafts excluded. */
export const publishedConcepts = conceptPosts.filter((p) => !p.draft);

export const conceptTopics = Array.from(
  new Set(publishedConcepts.flatMap((p) => p.topics)),
).sort();

export function getConcept(slug: string) {
  return conceptPosts.find((p) => p.slug === slug);
}

export function conceptsByRecency() {
  return [...publishedConcepts].sort((a, b) => (a.updated < b.updated ? 1 : -1));
}

export function sectionId(heading: string) {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function tocEntries(post: ConceptPost) {
  return post.sections.map((s) => ({ id: sectionId(s.heading), label: s.heading }));
}

export function formatConceptDate(iso: string) {
  return new Date(iso + "T00:00:00Z")
    .toLocaleDateString("en-US", { year: "numeric", month: "short", timeZone: "UTC" })
    .toUpperCase();
}
